import { useState, useEffect, useRef, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { getCurrentCompanyId } from '@/utils/auth';
import { editorAttachmentKeys, stripImageSrcForSave, ATTACHMENT_TYPES } from '@/utils/sop-helpers';
import { App } from '@/services/app-compat';

export interface EditSopModalProps {
  open: boolean;
  nocCode?: number | null;
  nocValue?: string;
  sopContent?: string;
  theme?: string;
  onSave: (data: {
    nocCode: number | null;
    sop: string;
    attachmentInfoList: { id: number; activationKey: string | null }[];
  }) => void;
  onCancel: () => void;
}

const sopToolbarConfig = App.isMobile() ? 'mobile' : [
  'formatting', 'fontName', 'fontSize',
  'bold', 'italic', 'underline', 'strikethrough',
  'backColor', 'foreColor', 'separator',
  'insertOrderedList', 'insertUnorderedList', 'indent', 'outdent',
  'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
  'lineHeight', 'separator',
  'createLink', 'unlink', 'insertImage', 'uploadImage', 'separator',
  'insertUpperRomanList', 'insertLowerRomanList',
  'createTable',
  'addRowAbove', 'addRowBelow', 'addColumnLeft', 'addColumnRight',
  'deleteRow', 'deleteColumn',
  'mergeCellsHorizontally', 'mergeCellsVertically',
  'splitCellHorizontally', 'splitCellVertically',
  'tableAlignLeft', 'tableAlignCenter', 'tableAlignRight', 'separator',
  'undo', 'redo', 'cleanFormatting', 'copyFormat', 'applyFormat',
];

export function EditSopModal({
  open,
  nocCode = null,
  nocValue = '',
  sopContent = '',
  theme,
  onSave,
  onCancel,
}: EditSopModalProps) {
  const [editorContent, setEditorContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const editorRef = useRef<RichTextEditorHandle>(null);
  const editorAttachmentKeysRef = useRef<Record<number, string>>({});

  // Load content with image processing
  const loadContent = useCallback(() => {
    const companyId = getCurrentCompanyId();
    const content = sopContent || '';

    if (typeof (window as any).loadNocSopImages === 'function') {
      (window as any).loadNocSopImages(
        content,
        nocCode,
        companyId,
        (processedContent: string) => {
          setEditorContent(processedContent);
          setIsLoading(false);
        }
      );
    } else {
      // Fallback if loadNocSopImages is not available
      setEditorContent(content);
      setIsLoading(false);
    }
  }, [sopContent, nocCode]);

  // Handle open/close
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      editorAttachmentKeysRef.current = {};
      setTimeout(() => {
        loadContent();
      }, 0);
    } else {
      setEditorContent('');
    }
  }, [open, loadContent]);

  const handleSave = useCallback(() => {
    const content = editorContent;
    const saveData = stripImageSrcForSave(content);

    const attachmentInfoList = saveData.attachmentInfoList.map((item) => ({
      id: item.id,
      activationKey: editorAttachmentKeys[item.id] || editorAttachmentKeysRef.current[item.id] || null,
    }));

    onSave({
      nocCode: nocCode ?? null,
      sop: saveData.content,
      attachmentInfoList: attachmentInfoList,
    });
  }, [editorContent, nocCode, onSave]);

  const handleImageUpload = useCallback((insertCallback: (imageUrl: string, alt?: string, width?: string, height?: string, attachmentId?: number | string) => void) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/gif,image/bmp,image/webp';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    fileInput.onchange = function () {
      if (!fileInput.files || fileInput.files.length === 0) {
        document.body.removeChild(fileInput);
        return;
      }

      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (_idx: number, attachmentInfo: any) => {
          editorAttachmentKeysRef.current[attachmentInfo.id] = attachmentInfo.activationKey || '';
          editorAttachmentKeys[attachmentInfo.id] = attachmentInfo.activationKey || '';
        },
        successCallback: (_idx: number, attachment: any) => {
          insertCallback(attachment.signedURL, undefined, undefined, undefined, attachment.id);
          document.body.removeChild(fileInput);
        },
        updateAttachment: () => {},
      }, ATTACHMENT_TYPES.SOP_EDITOR);
    };

    fileInput.click();
  }, []);

  const subheader = (
    <div className="kstc-sop-header">
      <span className="kstc-sop-noc-label">{nocValue}</span>
    </div>
  );

  return (
    <BaseFormModal
      open={open}
      title="Edit Standard Operating Procedure"
      theme={theme}
      dialogClass="kstc-dialog--sop-edit"
      bodyClass="kstc-sop-editor-body"
      subheader={subheader}
      onSave={handleSave}
      onCancel={onCancel}
    >
      {isLoading ? (
        <div className="kstc-sop-loading">Loading editor...</div>
      ) : (
        <RichTextEditor
          ref={editorRef}
          value={editorContent}
          height="500px"
          onImageUpload={handleImageUpload}
          toolbarConfig={sopToolbarConfig}
          placeholder=""
          onChange={(html: string) => setEditorContent(html)}
        />
      )}
    </BaseFormModal>
  );
}

(window as any).EditSopModalReact = EditSopModal;
